import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@weirgroup/weir-dls'

import { ReplacementPartType } from '../../types/bucketTypes'
import { SingleAssetGeofencesType } from '../../types/geofence.type'

import { ConfirmDialog } from '../confirm-dialog/confirm-dialog'

import assetsApi from '../../api/assets.api'
import geofenceApi from '../../api/geofence.api'
import { addToShoppingList } from '../../api/shopping-list.api'

import './styles.scss'
import { ToastDataType } from '../../types/toast.type'
import { GEOFENCE_SHAPE } from '../../constants/bucket.contants'
import { useDateConverter } from '../../hooks/use-date-converter'

interface Props {
    type: string
    cardInfo?: ReplacementPartType
    nonEsco?: boolean
    geofenceData?: SingleAssetGeofencesType
    fetchBucketList?: (arg?: boolean) => Promise<void>
    editHandler?: any
    setLoader?: Dispatch<SetStateAction<boolean>>
    renderToastMessage?: (toastData: ToastDataType) => void
    setIsAssetUpdated?: Dispatch<SetStateAction<boolean>>
    isGuest?: boolean,
    darkMode?: boolean
}

export const Card = (props: Props) => {
    const {
        type,
        cardInfo,
        nonEsco,
        geofenceData,
        fetchBucketList,
        setLoader,
        renderToastMessage,
        editHandler,
        setIsAssetUpdated,
        isGuest,
        darkMode,
    } = props
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
    const { t } = useTranslation()
    const { convertDateStringToFormat } = useDateConverter()

    const addItemToShoppingList = () => {
        if (setLoader) {
            setLoader(true)
            if (cardInfo) {
                addToShoppingList(cardInfo.part_id)
                    .then(() => {
                        let result = {
                            data: t('AddToListSuccess'),
                        }
                        triggerToastMessage(true, result)
                        setLoader(false)
                    })
                    .catch((err: any) => {
                        setLoader(false)
                        triggerToastMessage(false, err)
                        console.error(err)
                    })
            }
        }
    }

    const deletePart = () => {
        if (cardInfo?.part_id && fetchBucketList) {
            assetsApi
                .deleteReplacementPart(cardInfo.part_id)
                .then((result: any) => {
                    //TODO: Fetch specific asset's data once microservice supports it
                    //trigger refetch
                    fetchBucketList(true)
                    setIsConfirmOpen(false)
                })
                .catch((err: any) => {
                    setIsConfirmOpen(false)
                    console.error(err)
                })
        }
    }

    const triggerToastMessage = (success: boolean, result?: any) => {
        if (renderToastMessage) {
            const toastData: ToastDataType = {
                message: success ? result.data : result.response.data.message,
                variant: success ? 'success' : 'critical',
            }
            renderToastMessage(toastData)
        }
    }

    const deleteGeofence = () => {
        if (geofenceData && setLoader) {
            setLoader(true)
            geofenceApi
                .deleteGeofence(geofenceData.geofence_id)
                .then((result: any) => {
                    if (setIsAssetUpdated) setIsAssetUpdated(true)
                    triggerToastMessage(true, result)
                    setLoader(false)
                })
                .catch((err: any) => {
                    console.error(err)
                    if (setIsAssetUpdated) setIsAssetUpdated(true)
                    triggerToastMessage(false, err)
                    setLoader(false)
                })
        }
    }

    const renderActionButtons = (isReplacementParts?: boolean) => {
        return (
            <>
                <div
                    className={
                        isGuest ? 'isGuest btn-container' : 'btn-container'
                    }
                    onClick={() =>
                        isReplacementParts
                            ? editHandler(true, cardInfo)
                            : editHandler(geofenceData)
                    }
                    role="button"
                >
                    <Icon name={'Edit'} darkMode={darkMode}/>
                </div>
                <div
                    className={
                        isGuest ? 'isGuest btn-container' : 'btn-container'
                    }
                    onClick={() => setIsConfirmOpen(true)}
                    role="button"
                >
                    <Icon name={'Trash'} darkMode={darkMode} />
                </div>
            </>
        )
    }

    const replaceablePartCard = () => {
        return (
            <div className="card-container">
                {isConfirmOpen && cardInfo ? (
                    <ConfirmDialog
                        darkMode={darkMode}
                        isConfirmOpen={isConfirmOpen}
                        cancelHandler={() => setIsConfirmOpen(false)}
                        confirmHandler={deletePart}
                    />
                ) : (
                    <></>
                )}
                <div
                    className={
                        nonEsco
                            ? 'color-status gray-status-bg'
                            : 'color-status red-status-bg'
                    }
                ></div>
                <div className="details">
                    <div className="header">
                        <div className="title">
                            <p>{cardInfo?.description}</p>
                        </div>
                        <div className="sub-details">
                            <p>
                                {t('quantity')}:{' '}
                                <span>{cardInfo?.quantity}</span>
                            </p>
                        </div>
                    </div>
                    <div className="body">
                        <div className="info">
                            <p className="category">
                                {t('partNumber')}:{' '}
                                <span>{cardInfo?.part_number}</span>
                            </p>
                            <p className="category">
                                {t('pmid')}: <span>{cardInfo?.pmid}</span>
                            </p>
                            {cardInfo?.notes ? (
                                <p className="category">
                                    {t('notes')}: <span>{cardInfo?.notes}</span>
                                </p>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div
                            className={
                                nonEsco
                                    ? 'icons non-esco-flex'
                                    : 'icons'
                            }
                        >
                            {nonEsco ? (
                                <>{renderActionButtons(true)}</>
                            ) : (
                                <div
                                    className={
                                        isGuest
                                            ? 'isGuest btn-container'
                                            : 'btn-container'
                                    }
                                    onClick={() => addItemToShoppingList()}
                                    role="button"
                                >
                                    <Icon name={'DXPPlus'} darkMode={darkMode} />{' '}
                                    {t('addToShoppingList')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const geofenceCard = (geofenceData: SingleAssetGeofencesType) => {
        return (
            <div className="card-container">
                {isConfirmOpen ? (
                    <ConfirmDialog
                        isConfirmOpen={isConfirmOpen}
                        confirmHandler={() => deleteGeofence()}
                        cancelHandler={() => setIsConfirmOpen(false)}
                    />
                ) : (
                    <></>
                )}
                <div className="color-status red-status-bg"></div>
                <div className="details">
                    <div className="header">
                        <div className="title">
                            <p>
                                {t('geofence')}: {geofenceData?.name}
                            </p>
                        </div>
                        <div className="sub-details font-color-black">
                            <p>
                                {t('updated')}:{' '}
                                <span>
                                    {convertDateStringToFormat(geofenceData.updated_at)}
                                </span>
                            </p>
                        </div>
                    </div>
                    <div className="body geofence-body">
                        <div className="geofence-flex font-color-gray">
                            {geofenceData?.shape == GEOFENCE_SHAPE.CIRCLE ? (
                                <p className="category ">
                                    {t('radius')}:{' '}
                                    <span>{geofenceData?.radius}</span>
                                </p>
                            ) : (
                                <></>
                            )}
                            <p className="category">
                                {t('created')}:{' '}
                                <span>
                                    {geofenceData
                                        ? convertDateStringToFormat(geofenceData.created_at)
                                        : ''}
                                </span>
                            </p>
                            <p className="category">
                                {t('shape')}: <span>{geofenceData?.shape}</span>
                            </p>
                        </div>
                        <div className="icons add-to-shopping-list-flex">
                            {renderActionButtons()}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return type === 'geofence' && geofenceData
        ? geofenceCard(geofenceData)
        : replaceablePartCard()
}
